import {
  ref, set, get, push, update, remove, query,
  orderByChild, equalTo, limitToLast, serverTimestamp
} from 'firebase/database';
import { db } from './config';

const snap2arr = (snap) => {
  if (!snap.exists()) return [];
  const data = snap.val();
  return Object.keys(data).map(k => ({ id: k, ...data[k] }));
};

// ─── PRODUCTS ────────────────────────────────────────────────────────────────
export const getProducts = async (filters = {}) => {
  const snap = await get(ref(db, 'products'));
  let products = snap2arr(snap);
  if (filters.category) products = products.filter(p => p.collectionId === filters.category || p.category === filters.category);
  if (filters.inStock)  products = products.filter(p => p.stock > 0);
  if (filters.search)   products = products.filter(p =>
    p.title?.toLowerCase().includes(filters.search.toLowerCase()) ||
    p.description?.toLowerCase().includes(filters.search.toLowerCase())
  );
  if (filters.orderByField === 'price') {
    products.sort((a, b) => filters.orderDir === 'desc' ? b.price - a.price : a.price - b.price);
  } else if (filters.orderByField === 'rating') {
    products.sort((a, b) => (b.rating || 0) - (a.rating || 0));
  } else {
    products.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }
  if (filters.limitCount) products = products.slice(0, filters.limitCount);
  return products;
};

export const getProduct = async (id) => {
  const snap = await get(ref(db, `products/${id}`));
  return snap.exists() ? { id, ...snap.val() } : null;
};

export const addProduct = async (data) => {
  const newRef = push(ref(db, 'products'));
  await set(newRef, { ...data, createdAt: Date.now(), updatedAt: Date.now() });
  return { id: newRef.key };
};

export const updateProduct = async (id, data) => {
  await update(ref(db, `products/${id}`), { ...data, updatedAt: Date.now() });
};

export const deleteProduct = async (id) => {
  await remove(ref(db, `products/${id}`));
};

// ─── COLLECTIONS ─────────────────────────────────────────────────────────────
export const getCollections = async () => {
  const snap = await get(ref(db, 'collections'));
  return snap2arr(snap).sort((a, b) => (a.order || 0) - (b.order || 0));
};
export const addCollection = async (data) => {
  const newRef = push(ref(db, 'collections'));
  await set(newRef, { ...data, createdAt: Date.now() });
  return { id: newRef.key };
};
export const updateCollection = async (id, data) => update(ref(db, `collections/${id}`), data);
export const deleteCollection = async (id) => remove(ref(db, `collections/${id}`));

// ─── ORDERS ──────────────────────────────────────────────────────────────────
export const placeOrder = async (orderData) => {
  const newRef = push(ref(db, 'orders'));
  await set(newRef, { ...orderData, status: 'pending', createdAt: Date.now(), updatedAt: Date.now() });
  for (const item of orderData.items) {
    const prodSnap = await get(ref(db, `products/${item.id}`));
    if (prodSnap.exists()) {
      const currentStock = prodSnap.val().stock || 0;
      await update(ref(db, `products/${item.id}`), { stock: Math.max(0, currentStock - item.quantity), updatedAt: Date.now() });
    }
  }
  return { id: newRef.key };
};

export const getOrders = async (uid = null) => {
  const snap = await get(ref(db, 'orders'));
  let orders = snap2arr(snap);
  if (uid) orders = orders.filter(o => o.userId === uid);
  orders.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  return orders;
};

export const getOrder = async (id) => {
  const snap = await get(ref(db, `orders/${id}`));
  return snap.exists() ? { id, ...snap.val() } : null;
};

export const updateOrderStatus = async (id, status) => {
  await update(ref(db, `orders/${id}`), { status, updatedAt: Date.now() });
};

// ─── REVIEWS ─────────────────────────────────────────────────────────────────
export const getReviews = async (productId) => {
  const snap = await get(ref(db, 'reviews'));
  const all = snap2arr(snap);
  return all.filter(r => r.productId === productId).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
};

// All reviews across every product, for the admin moderation screen.
export const getAllReviews = async () => {
  const snap = await get(ref(db, 'reviews'));
  return snap2arr(snap).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
};

const recalcProductRating = async (productId) => {
  const remaining = await getReviews(productId);
  if (remaining.length === 0) {
    await update(ref(db, `products/${productId}`), { rating: 0, reviewCount: 0, updatedAt: Date.now() });
    return;
  }
  const avg = remaining.reduce((s, r) => s + r.rating, 0) / remaining.length;
  await update(ref(db, `products/${productId}`), { rating: parseFloat(avg.toFixed(1)), reviewCount: remaining.length, updatedAt: Date.now() });
};

export const addReview = async (data) => {
  const newRef = push(ref(db, 'reviews'));
  await set(newRef, { ...data, createdAt: Date.now() });
  await recalcProductRating(data.productId);
  return { id: newRef.key };
};

// Admin deletes a review (spam, abuse, etc.) — the product's rating average and
// review count are automatically recalculated from what's left.
export const deleteReview = async (reviewId, productId) => {
  await remove(ref(db, `reviews/${reviewId}`));
  if (productId) await recalcProductRating(productId);
};

// ─── USERS ───────────────────────────────────────────────────────────────────
export const createUserRecord = async (uid, data) => {
  await set(ref(db, `users/${uid}`), { ...data, role: 'user', createdAt: Date.now(), wishlist: [], orders: [] });
};
export const getUsers = async () => {
  const snap = await get(ref(db, 'users'));
  return snap2arr(snap).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
};
export const getUserData = async (uid) => {
  const snap = await get(ref(db, `users/${uid}`));
  return snap.exists() ? { id: uid, ...snap.val() } : null;
};
export const getUserRole = async (uid) => {
  const snap = await get(ref(db, `users/${uid}/role`));
  return snap.exists() ? snap.val() : 'user';
};
export const updateUserRole = async (uid, role) => {
  await update(ref(db, `users/${uid}`), { role, updatedAt: Date.now() });
};

// ─── FAQs ─────────────────────────────────────────────────────────────────────
export const getFAQs = async () => {
  const snap = await get(ref(db, 'faqs'));
  return snap2arr(snap).sort((a, b) => (a.order || 0) - (b.order || 0));
};
export const addFAQ = async (data) => { const r = push(ref(db, 'faqs')); await set(r, { ...data, createdAt: Date.now() }); return { id: r.key }; };
export const updateFAQ = async (id, data) => update(ref(db, `faqs/${id}`), data);
export const deleteFAQ = async (id) => remove(ref(db, `faqs/${id}`));

// ─── SITE SETTINGS ────────────────────────────────────────────────────────────
export const getSiteSettings = async () => {
  const snap = await get(ref(db, 'settings/general'));
  return snap.exists() ? snap.val() : {};
};
export const updateSiteSettings = async (data) => update(ref(db, 'settings/general'), data);

export const getMetaSettings = async () => {
  const snap = await get(ref(db, 'settings/meta'));
  return snap.exists() ? snap.val() : {};
};
export const updateMetaSettings = async (data) => update(ref(db, 'settings/meta'), data);

// ─── SHIPPING SETTINGS ────────────────────────────────────────────────────────
export const getShippingSettings = async () => {
  const snap = await get(ref(db, 'settings/shipping'));
  if (snap.exists()) return snap.val();
  return {
    rules: [
      { id: 'r1', city: 'Karachi', minQty: 1, maxQty: 2, price: 200, label: 'Karachi (1-2 pcs)', codAllowed: true },
      { id: 'r2', city: 'Karachi', minQty: 3, maxQty: 999, price: 300, label: 'Karachi (3+ pcs)', codAllowed: false },
      { id: 'r3', city: 'Other', minQty: 1, maxQty: 2, price: 350, label: 'Other Cities (1-2 pcs)', codAllowed: false },
      { id: 'r4', city: 'Other', minQty: 3, maxQty: 999, price: 500, label: 'Other Cities (3+ pcs)', codAllowed: false },
    ],
    freeShippingThreshold: 0,
    paymentAccounts: {
      jazzcash: { number: '', name: '' },
      easypaisa: { number: '', name: '' },
      bank: { bankName: '', accountNo: '', accountTitle: '', iban: '' }
    }
  };
};
export const updateShippingSettings = async (data) => update(ref(db, 'settings/shipping'), data);

// ─── DASHBOARD STATS ──────────────────────────────────────────────────────────
export const getDashboardStats = async () => {
  const [ordersSnap, productsSnap, usersSnap] = await Promise.all([
    get(ref(db, 'orders')), get(ref(db, 'products')), get(ref(db, 'users'))
  ]);
  const orders = snap2arr(ordersSnap);
  const products = snap2arr(productsSnap);
  const users = snap2arr(usersSnap);
  const revenue = orders.filter(o => o.status !== 'cancelled').reduce((s, o) => s + (o.total || 0), 0);
  const customers = users.filter(u => u.role !== 'admin');
  const monthlyRevenue = Array.from({ length: 6 }, (_, i) => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    const label = d.toLocaleDateString('en-PK', { month: 'short' });
    const monthOrders = orders.filter(o => {
      const od = new Date(o.createdAt);
      return od.getMonth() === d.getMonth() && od.getFullYear() === d.getFullYear() && o.status !== 'cancelled';
    });
    return { label, revenue: monthOrders.reduce((s, o) => s + (o.total || 0), 0), orders: monthOrders.length };
  });
  const statusBreakdown = ['pending','confirmed','processing','shipped','delivered','cancelled'].reduce((acc, s) => {
    acc[s] = orders.filter(o => o.status === s).length; return acc;
  }, {});
  return {
    totalOrders: orders.length, totalRevenue: revenue,
    totalProducts: products.length, totalCustomers: customers.length,
    totalAdmins: users.filter(u => u.role === 'admin').length,
    recentOrders: [...orders].sort((a,b) => b.createdAt - a.createdAt).slice(0, 8),
    monthlyRevenue, statusBreakdown,
    lowStockProducts: products.filter(p => p.stock <= 5 && p.stock > 0).length,
    outOfStock: products.filter(p => p.stock <= 0).length,
  };
};

export const ORDER_STATUSES = ['pending','confirmed','processing','shipped','delivered','cancelled'];
